import React, { Fragment, useState } from "react";
import { VARIANT } from "src/Modules/ThemeModule/Types/CommonTypes";
import Block from "./Block";

type FastPictureProps = {
  source: string; // Use `src` instead of `source` for web
  tintVariant?: VARIANT;
  useLoadingIndicator?: boolean;
  borderRadius?: number | string;
  resizeMode?: "cover" | "contain" | "stretch" | "repeat" | "none";
  onLoad?: () => void;
  onError?: () => void;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  testID?: string;
  accessibilityLabel?: string;
  style?: React.CSSProperties;
} & React.CSSProperties &
  React.HTMLAttributes<HTMLImageElement>; // Add support for CSS properties

function FastPicture(props: FastPictureProps) {
  const {
    source,
    style,
    tintVariant,
    useLoadingIndicator = false,
    borderRadius,
    resizeMode = "contain",
    onLoad,
    onError,
    onLoadStart,
    onLoadEnd,
    testID,
    accessibilityLabel,
    ...imageStyleProps // Spread all CSS properties
  } = props;

  const getImageStyle = () => {
    if (!resizeMode) return {}; // Return empty object if resizeMode is not provided

    switch (resizeMode) {
      case "cover":
        return { objectFit: "cover" };
      case "contain":
        return { objectFit: "contain" };
      case "stretch":
        return { objectFit: "fill" }; // Stretch the image to fill the container
      case "repeat":
        return {
          objectFit: "none",
          backgroundSize: "repeat",
          backgroundImage: `url(${source})`,
        }; // Repeat the image
      case "none":
        return { objectFit: "none" };
      default:
        return {}; // Default to no objectFit if resizeMode is invalid
    }
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Handle image load events
  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    onError?.();
  };

  const handleLoadStart = () => {
    setIsLoading(true);
    onLoadStart?.();
  };

  const handleLoadEnd = () => {
    setIsLoading(false);
    onLoadEnd?.();
  };

  // Determine the image styles
  //@ts-ignore
  const imageStyle: React.CSSProperties = {
    borderRadius: borderRadius,
    width: "100%",
    height: "100%",
    ...getImageStyle(),
    ...imageStyleProps, // Spread all CSS properties
    ...style, // Allow style prop to override CSS properties
  };

  return (
    <Fragment>
      <img
        src={source}
        alt={accessibilityLabel}
        onLoad={handleLoad}
        onError={handleError}
        onLoadStart={handleLoadStart}
        onLoadedData={handleLoadEnd}
        style={imageStyle}
        {...imageStyleProps}
      />
      {useLoadingIndicator && isLoading ? (
        <Block
          position="absolute"
          top={0}
          width={"100%"}
          overflow="hidden"
          height={"100%"}
          borderRadius={borderRadius}
        >
          {/* Web-compatible skeleton loader */}
          <div
            className="skeleton-loader"
            style={{ width: "100%", height: "100%" }}
          />
        </Block>
      ) : null}
    </Fragment>
  );
}

export default FastPicture;
